/**
 * operatorComparative.ts — MER Algo Footprints v8.6.2
 *
 * Deterministic Comparative Intelligence Layer.
 *
 * Consumes SessionIntelligenceSummary[] + CoachingReport to produce:
 *   - SessionRanking[]          — deterministic rank + percentile per session
 *   - SessionComparison[]       — structured pairwise comparison (vs baseline)
 *   - SessionCluster[]          — best / worst session clusters with shared traits
 *   - EdgeValidation[]          — signal family effectiveness with confidence
 *   - ConditionPerformance[]    — performance by regime / context condition
 *   - ComparativeGuidance       — validated-evidence coaching guidance
 *   - ComparativeReport         — complete comparative intelligence digest
 *
 * ── Design rules ──────────────────────────────────────────────────────────────
 * 1. Pure function — same inputs always produce same outputs
 * 2. No Date.now() in semantic output
 * 3. All thresholds in COMPARATIVE_THRESHOLDS — no scattered magic numbers
 * 4. Structured-first — no reasoning-string parsing
 * 5. Conservative — minimum sample sizes before conclusions drawn
 *
 * ── Input sources ────────────────────────────────────────────────────────────
 *   SessionIntelligenceSummary[]  — ordered oldest → newest
 *   CoachingReport                — from buildCoachingReport() (optional enrichment)
 */

import type { SessionIntelligenceSummary } from './operatorIntelligence';
import type { CoachingReport } from './operatorCoaching';

// ── Centralized thresholds ────────────────────────────────────────────────────

export const COMPARATIVE_THRESHOLDS = {
  // Session ranking categories (percentile boundaries)
  RANK_TOP_PERCENTILE:       80,   // ≥ 80th = 'top'
  RANK_ABOVE_AVG_PERCENTILE: 60,   // ≥ 60th = 'above_avg'
  RANK_AVERAGE_PERCENTILE:   40,   // ≥ 40th = 'average'
  RANK_BELOW_AVG_PERCENTILE: 20,   // ≥ 20th = 'below_avg'; < 20th = 'poor'

  // Cluster analysis
  CLUSTER_TOP_FRACTION:      0.33, // top third = 'best' cluster
  CLUSTER_BOTTOM_FRACTION:   0.33, // bottom third = 'worst' cluster
  CLUSTER_MIN_SESSIONS:      2,    // minimum sessions per cluster

  // Edge validation
  EDGE_HIGH_CONFIDENCE_MIN:  5,    // ≥ 5 decision occurrences = high confidence
  EDGE_MEDIUM_CONFIDENCE_MIN: 3,   // ≥ 3 = medium confidence
  EDGE_VALIDATED_WIN_RATE:   0.65, // ≥ 65% = validated
  EDGE_NOT_EFFECTIVE_WIN_RATE: 0.40, // ≤ 40% = not_effective

  // Condition performance
  CONDITION_MIN_SESSIONS:    2,    // min sessions to include a condition

  // Comparison significance
  COMPARISON_MATERIAL_DELTA: 5.0,  // score delta ≥ 5 pts = material difference

  // Baseline selection: use median session as baseline for pairwise comparison
} as const;

const T = COMPARATIVE_THRESHOLDS;

// ── Output types ──────────────────────────────────────────────────────────────

export type RankCategory = 'top' | 'above_avg' | 'average' | 'below_avg' | 'poor';
export type EdgeConclusion = 'validated' | 'inconclusive' | 'not_effective';
export type EdgeConfidence = 'low' | 'medium' | 'high';

export interface SessionRanking {
  /** Stable session identifier — symbol + index */
  sessionId:   string;
  score:       number;
  rank:        number;       // 1 = best
  percentile:  number;       // 0–100
  category:    RankCategory;
}

export interface SessionComparison {
  baselineSessionId:  string;
  comparedSessionId:  string;
  differences: {
    scoreDelta:          number;
    calibrationDelta:    number | null;
    signalQualityDelta:  number | null;
    winRateDelta:        number | null;
  };
  /** Whether the compared session is materially better / worse / similar */
  verdict:     'better' | 'worse' | 'similar';
  summary:     string;
}

export interface SessionCluster {
  type:                   'best' | 'worst';
  sessionIds:             string[];
  /** Shared structured traits from patternInsights.key */
  sharedCharacteristics:  string[];
  avgScore:               number;
  summary:                string;
}

export interface EdgeValidation {
  /** e.g. 'layer:gamma', 'calibration:overconfident' */
  signalFamily: string;
  context:      string;
  /** Win rate expressed as 0–100 score */
  performance:  number;
  sampleSize:   number;
  confidence:   EdgeConfidence;
  conclusion:   EdgeConclusion;
  reasoning:    string;
}

export interface ConditionPerformance {
  /** e.g. 'gamma', 'trap', 'multi_driver' */
  condition:    string;
  sessions:     number;
  avgScore:     number;
  trend:        'improving' | 'flat' | 'degrading';
  winRate:      number | null;
}

export interface ComparativeGuidance {
  /** Evidence-backed validated signal families */
  validatedEdges:    string[];
  /** Signal families with consistently poor outcomes */
  weakEdges:         string[];
  /** Condition/context combinations to avoid */
  avoidConditions:   string[];
  /** Condition/context combinations where edge is strongest */
  bestConditions:    string[];
  notes:             string[];
}

export interface ComparativeReport {
  symbol:               string;
  sessionCount:         number;
  rankings:             SessionRanking[];
  comparisons:          SessionComparison[];
  clusters:             SessionCluster[];
  edgeValidations:      EdgeValidation[];
  conditionPerformance: ConditionPerformance[];
  guidance:             ComparativeGuidance;
  reasoning:            string[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function _round1(v: number): number { return Math.round(v * 10) / 10; }
function _round2(v: number): number { return Math.round(v * 100) / 100; }

function _sessionId(symbol: string, index: number): string {
  return `${symbol}:session:${index}`;
}

// ── Step 1: Session ranking ───────────────────────────────────────────────────

function _buildRankings(sessions: SessionIntelligenceSummary[]): SessionRanking[] {
  const n = sessions.length;
  if (n === 0) return [];

  // Sort descending by score to assign ranks
  const indexed = sessions.map((s, i) => ({ s, i }));
  const sorted  = [...indexed].sort((a, b) => b.s.sessionScore.overall - a.s.sessionScore.overall);

  return sorted.map((item, rankIdx) => {
    const rank       = rankIdx + 1;
    const percentile = _round1((n - rank) / Math.max(n - 1, 1) * 100);
    const category: RankCategory =
      percentile >= T.RANK_TOP_PERCENTILE       ? 'top' :
      percentile >= T.RANK_ABOVE_AVG_PERCENTILE ? 'above_avg' :
      percentile >= T.RANK_AVERAGE_PERCENTILE   ? 'average' :
      percentile >= T.RANK_BELOW_AVG_PERCENTILE ? 'below_avg' : 'poor';

    return {
      sessionId:  _sessionId(item.s.symbol, item.i),
      score:      item.s.sessionScore.overall,
      rank,
      percentile,
      category,
    };
  }).sort((a, b) => a.rank - b.rank); // stable: rank ascending
}

// ── Step 2: Session comparisons ───────────────────────────────────────────────

function _buildComparisons(
  sessions:  SessionIntelligenceSummary[],
  rankings:  SessionRanking[],
): SessionComparison[] {
  if (sessions.length < 2) return [];

  // Baseline = median session (by rank)
  const midRank     = Math.ceil(rankings.length / 2);
  const baselineRec = rankings.find(r => r.rank === midRank) ?? rankings[midRank - 1];
  const baselineIdx = parseInt(baselineRec.sessionId.split(':').pop() ?? '0', 10);
  const baseline    = sessions[baselineIdx];

  return sessions.map((s, i) => {
    const compared = _sessionId(s.symbol, i);
    if (compared === baselineRec.sessionId) return null;

    const scoreDelta = _round1(s.sessionScore.overall - baseline.sessionScore.overall);
    const calibDelta = (s.sessionScore.components.calibration != null && baseline.sessionScore.components.calibration != null)
      ? _round1(s.sessionScore.components.calibration - baseline.sessionScore.components.calibration)
      : null;
    const qualityDelta = (s.sessionScore.components.signalClarity != null && baseline.sessionScore.components.signalClarity != null)
      ? _round1(s.sessionScore.components.signalClarity - baseline.sessionScore.components.signalClarity)
      : null;
    const wrDelta = (s.sessionScore.components.winRate != null && baseline.sessionScore.components.winRate != null)
      ? _round1(s.sessionScore.components.winRate - baseline.sessionScore.components.winRate)
      : null;

    const verdict: SessionComparison['verdict'] =
      Math.abs(scoreDelta) < T.COMPARISON_MATERIAL_DELTA ? 'similar' :
      scoreDelta > 0 ? 'better' : 'worse';

    const parts: string[] = [`vs median: score ${scoreDelta >= 0 ? '+' : ''}${scoreDelta}`];
    if (calibDelta !== null) parts.push(`calib ${calibDelta >= 0 ? '+' : ''}${calibDelta}`);
    if (wrDelta    !== null) parts.push(`win rate ${wrDelta >= 0 ? '+' : ''}${wrDelta}`);

    return {
      baselineSessionId: baselineRec.sessionId,
      comparedSessionId: compared,
      differences:       { scoreDelta, calibrationDelta: calibDelta, signalQualityDelta: qualityDelta, winRateDelta: wrDelta },
      verdict,
      summary:           parts.join(' | '),
    } satisfies SessionComparison;
  }).filter((c): c is SessionComparison => c !== null);
}

// ── Step 3: Best / worst clusters ────────────────────────────────────────────

function _buildClusters(
  sessions:  SessionIntelligenceSummary[],
  rankings:  SessionRanking[],
): SessionCluster[] {
  const n = sessions.length;
  if (n < T.CLUSTER_MIN_SESSIONS * 2) return [];

  const clusterSize = Math.max(T.CLUSTER_MIN_SESSIONS, Math.floor(n * T.CLUSTER_TOP_FRACTION));

  // Best = top clusterSize sessions by rank
  const bestRanks  = rankings.slice(0, clusterSize);
  const worstRanks = rankings.slice(rankings.length - clusterSize);

  function _buildCluster(ranks: SessionRanking[], type: 'best' | 'worst'): SessionCluster {
    const ids     = ranks.map(r => r.sessionId);
    const idxs    = ids.map(id => parseInt(id.split(':').pop() ?? '0', 10));
    const subs    = idxs.map(i => sessions[i]).filter(Boolean);
    const avgScore = _round1(ranks.reduce((s, r) => s + r.score, 0) / ranks.length);

    // Shared characteristics: pattern keys that appear in ALL sessions in this cluster
    const allPatternKeys = subs.map(s => new Set(s.patternInsights.map(p => p.key)));
    const sharedKeys: string[] = allPatternKeys.length > 0
      ? [...allPatternKeys[0]].filter(k => allPatternKeys.every(set => set.has(k)))
      : [];

    const summary = `${type === 'best' ? 'Top' : 'Bottom'} ${ranks.length} sessions: avg score ${avgScore}${sharedKeys.length > 0 ? `, shared: ${sharedKeys.slice(0, 3).join(', ')}` : ''}`;

    return { type, sessionIds: ids, sharedCharacteristics: sharedKeys.slice(0, 5), avgScore, summary };
  }

  return [_buildCluster(bestRanks, 'best'), _buildCluster(worstRanks, 'worst')];
}

// ── Step 4: Edge validation ───────────────────────────────────────────────────

function _buildEdgeValidations(sessions: SessionIntelligenceSummary[]): EdgeValidation[] {
  const validations: EdgeValidation[] = [];

  // Aggregate win rates per pattern key across all sessions
  const keyWins:   Record<string, number> = {};
  const keyActedOn: Record<string, number> = {};
  const keyTotal:   Record<string, number> = {};

  for (const s of sessions) {
    for (const p of s.patternInsights) {
      keyTotal[p.key]   = (keyTotal[p.key]   ?? 0) + p.frequency;
      if (p.winRate !== null) {
        const actedEst  = p.frequency;
        keyWins[p.key]    = (keyWins[p.key]    ?? 0) + p.winRate * actedEst;
        keyActedOn[p.key] = (keyActedOn[p.key] ?? 0) + actedEst;
      }
    }
  }

  for (const key of Object.keys(keyActedOn)) {
    const sampleSize = Math.round(keyActedOn[key]);
    if (sampleSize < 1) continue;

    const winRate    = _round2(keyWins[key] / keyActedOn[key]);
    const confidence: EdgeConfidence =
      sampleSize >= T.EDGE_HIGH_CONFIDENCE_MIN   ? 'high' :
      sampleSize >= T.EDGE_MEDIUM_CONFIDENCE_MIN ? 'medium' : 'low';

    const conclusion: EdgeConclusion =
      confidence === 'low'                      ? 'inconclusive' :
      winRate >= T.EDGE_VALIDATED_WIN_RATE       ? 'validated' :
      winRate <= T.EDGE_NOT_EFFECTIVE_WIN_RATE   ? 'not_effective' : 'inconclusive';

    validations.push({
      signalFamily: key,
      context:      key.split(':')[0] ?? 'unknown',
      performance:  _round1(winRate * 100),
      sampleSize,
      confidence,
      conclusion,
      reasoning:    `${key}: ${sampleSize} samples, ${(winRate * 100).toFixed(0)}% win rate → ${conclusion} (${confidence} confidence)`,
    });
  }

  // Sort: validated first, then by performance desc, then by sample size desc
  const conclusionOrder: Record<EdgeConclusion, number> = { validated: 3, inconclusive: 2, not_effective: 1 };
  return validations.sort((a, b) =>
    conclusionOrder[b.conclusion] - conclusionOrder[a.conclusion] ||
    b.performance - a.performance ||
    b.sampleSize - a.sampleSize
  );
}

// ── Step 5: Condition performance ────────────────────────────────────────────

function _buildConditionPerformance(sessions: SessionIntelligenceSummary[]): ConditionPerformance[] {
  // Group sessions by their dominant attribution layer (strongest structured signal context)
  const condGroups: Record<string, SessionIntelligenceSummary[]> = {};

  for (const s of sessions) {
    // Primary condition: dominant pattern key (e.g. 'layer:gamma')
    const dominant = s.patternInsights.find(p => p.reliable)?.key ??
      s.patternInsights.sort((a, b) => b.frequency - a.frequency)[0]?.key ??
      'mixed';
    const cond = dominant.replace('layer:', '').replace('calibration:', '').replace(':', '_');
    if (!condGroups[cond]) condGroups[cond] = [];
    condGroups[cond].push(s);
  }

  const results: ConditionPerformance[] = [];
  for (const [cond, subs] of Object.entries(condGroups)) {
    if (subs.length < T.CONDITION_MIN_SESSIONS) continue;

    const scores  = subs.map(s => s.sessionScore.overall).filter(v => v > 0);
    const avgScore = scores.length > 0 ? _round1(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;

    // Trend from first to last session in this condition
    const first = scores[0] ?? 0;
    const last  = scores[scores.length - 1] ?? 0;
    const delta = last - first;
    const trend: ConditionPerformance['trend'] = delta >= 5 ? 'improving' : delta <= -5 ? 'degrading' : 'flat';

    // Win rate aggregate
    const wrs   = subs.map(s => s.sessionScore.components.winRate).filter((v): v is number => v !== null);
    const winRate = wrs.length > 0 ? _round2(wrs.reduce((a, b) => a + b, 0) / wrs.length / 100) : null;

    results.push({ condition: cond, sessions: subs.length, avgScore, trend, winRate });
  }

  return results.sort((a, b) => b.avgScore - a.avgScore);
}

// ── Step 6: Comparative guidance ─────────────────────────────────────────────

function _buildComparativeGuidance(
  edgeValidations:      EdgeValidation[],
  conditionPerformance: ConditionPerformance[],
  clusters:             SessionCluster[],
  coaching:             CoachingReport | null,
): ComparativeGuidance {
  const validatedEdges:  string[] = [];
  const weakEdges:       string[] = [];
  const avoidConditions: string[] = [];
  const bestConditions:  string[] = [];
  const notes:           string[] = [];

  // From edge validation
  for (const ev of edgeValidations.filter(e => e.conclusion === 'validated')) {
    validatedEdges.push(`${ev.signalFamily} — ${(ev.performance).toFixed(0)}% win rate, ${ev.sampleSize} samples (${ev.confidence} confidence)`);
  }
  for (const ev of edgeValidations.filter(e => e.conclusion === 'not_effective')) {
    weakEdges.push(`${ev.signalFamily} — ${ev.performance.toFixed(0)}% win rate (below baseline), ${ev.sampleSize} samples`);
  }

  // From condition performance
  const sortedCond = [...conditionPerformance].sort((a, b) => b.avgScore - a.avgScore);
  for (const c of sortedCond.slice(0, 2)) {
    if (c.avgScore > 55) bestConditions.push(`${c.condition} environments (avg score ${c.avgScore}, ${c.sessions} sessions)`);
  }
  for (const c of sortedCond.slice(-2)) {
    if (c.avgScore < 50) avoidConditions.push(`${c.condition} environments (avg score ${c.avgScore}, ${c.sessions} sessions)`);
  }

  // From clusters
  const best  = clusters.find(c => c.type === 'best');
  const worst = clusters.find(c => c.type === 'worst');
  if (best?.sharedCharacteristics.length) {
    notes.push(`Best sessions share: ${best.sharedCharacteristics.slice(0, 3).join(', ')}`);
  }
  if (worst?.sharedCharacteristics.length) {
    notes.push(`Worst sessions share: ${worst.sharedCharacteristics.slice(0, 3).join(', ')}`);
  }

  // From coaching (carry forward high-confidence items)
  if (coaching) {
    for (const s of coaching.strengths.slice(0, 1)) {
      if (!validatedEdges.some(e => e.startsWith(s.category))) {
        notes.push(`Coaching confirms ${s.category} as recurring strength`);
      }
    }
  }

  if (validatedEdges.length === 0 && weakEdges.length === 0) {
    notes.push('Accumulate more session data for edge validation conclusions');
  }

  return { validatedEdges, weakEdges, avoidConditions, bestConditions, notes };
}

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Build a deterministic Comparative Intelligence Report.
 *
 * PURE FUNCTION — same inputs always produce same outputs.
 * No Date.now(), no I/O, no side effects.
 *
 * @param symbol   — symbol or 'ALL'
 * @param sessions — SessionIntelligenceSummary[], ordered oldest → newest
 * @param coaching — CoachingReport (optional enrichment)
 */
export function buildComparativeReport(
  symbol:   string,
  sessions: SessionIntelligenceSummary[],
  coaching: CoachingReport | null = null,
): ComparativeReport {
  const n = sessions.length;

  if (n < 2) {
    return {
      symbol, sessionCount: n, rankings: [], comparisons: [], clusters: [],
      edgeValidations: [], conditionPerformance: [],
      guidance: { validatedEdges: [], weakEdges: [], avoidConditions: [], bestConditions: [],
        notes: [`Need ≥2 sessions for comparative analysis (have ${n})`] },
      reasoning: [`Insufficient sessions (${n})`],
    };
  }

  const rankings            = _buildRankings(sessions);
  const comparisons         = _buildComparisons(sessions, rankings);
  const clusters            = _buildClusters(sessions, rankings);
  const edgeValidations     = _buildEdgeValidations(sessions);
  const conditionPerformance = _buildConditionPerformance(sessions);
  const guidance            = _buildComparativeGuidance(edgeValidations, conditionPerformance, clusters, coaching);

  const topSession  = sessions[rankings[0] ? parseInt(rankings[0].sessionId.split(':').pop() ?? '0', 10) : 0];
  const validated   = edgeValidations.filter(e => e.conclusion === 'validated');
  const notEffect   = edgeValidations.filter(e => e.conclusion === 'not_effective');

  const reasoning: string[] = [
    `Sessions: ${n} | Rankings: ${rankings.length} | Clusters: ${clusters.length}`,
    `Best session: score ${rankings[0]?.score} (${rankings[0]?.category})`,
    `Edge validations: ${validated.length} validated, ${notEffect.length} not effective`,
    `Conditions analysed: ${conditionPerformance.length}`,
    `Top session dominant pattern: ${topSession?.patternInsights[0]?.key ?? '—'}`,
  ];

  return { symbol, sessionCount: n, rankings, comparisons, clusters, edgeValidations, conditionPerformance, guidance, reasoning };
}
